import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { Input } from "@/components/ui/input";
import { MapPin, Loader2 } from "lucide-react";

const PH = { fr: "Ville (tape pour rechercher)…", en: "City (type to search)…", es: "Ciudad (escribe para buscar)…", pt: "Cidade (digite para pesquisar)…" };
const NEED_COUNTRY = { fr: "Choisis d'abord un pays", en: "Choose a country first", es: "Elige primero un país", pt: "Escolha primeiro um país" };
const USE_FREE = { fr: "Utiliser", en: "Use", es: "Usar", pt: "Usar" };
const NONE = { fr: "Aucune ville trouvée", en: "No city found", es: "Ninguna ciudad encontrada", pt: "Nenhuma cidade encontrada" };

// Country-aware city autocomplete backed by /api/geo/cities (GeoNames).
// The results panel is rendered in a portal with fixed positioning so it is never
// clipped by scroll containers (e.g. the onboarding step with overflow-y-auto).
// Lot 658 : la liste descend jusqu'au bord visible (clavier ouvert → plus de bande blanche) et
// s'ouvre au-dessus du champ quand la place manque en dessous.
const PANEL_GAP = 4;
const PANEL_MARGIN = 8;
const PANEL_MIN = 120;
const PANEL_MAX = 420;
export const placeCityPanel = (r, viewportHeight) => {
  const below = viewportHeight - r.bottom - PANEL_GAP - PANEL_MARGIN;
  const above = r.top - PANEL_GAP - PANEL_MARGIN;
  const flip = below < 160 && above > below;
  const room = flip ? above : below;
  return { left: r.left, width: r.width, flip, top: flip ? undefined : r.bottom + PANEL_GAP, bottom: flip ? viewportHeight - r.top + PANEL_GAP : undefined, maxHeight: Math.max(PANEL_MIN, Math.min(PANEL_MAX, room)) };
};

export const CityAutocomplete = ({ value = "", country = "", onChange, testId = "city-autocomplete", placeholder }) => {
  const { lang } = useApp();
  const [q, setQ] = useState(value || "");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [rect, setRect] = useState(null);
  const reqRef = useRef(0);
  const wrapRef = useRef(null);
  const tr = (m) => m[lang] || m.fr;

  useEffect(() => { setQ(value || ""); }, [value]);

  const updateRect = () => {
    if (wrapRef.current) {
      const vh = window.visualViewport?.height || window.innerHeight;
      setRect(placeCityPanel(wrapRef.current.getBoundingClientRect(), vh));
    }
  };

  useLayoutEffect(() => {
    if (!open) return undefined;
    updateRect();
    const on = () => updateRect();
    window.addEventListener("scroll", on, true);
    window.addEventListener("resize", on);
    window.visualViewport?.addEventListener("resize", on);
    window.visualViewport?.addEventListener("scroll", on);
    return () => {
      window.removeEventListener("scroll", on, true);
      window.removeEventListener("resize", on);
      window.visualViewport?.removeEventListener("resize", on);
      window.visualViewport?.removeEventListener("scroll", on);
    };
  }, [open]);

  useEffect(() => {
    if (!open || !country) { setResults([]); return undefined; }
    setLoading(true);
    const rid = ++reqRef.current;
    const timer = setTimeout(() => {
      api.get("/geo/cities", { params: { country, q: (q || "").trim(), limit: 10 } })
        .then(({ data }) => { if (rid === reqRef.current) setResults(data?.cities || []); })
        .catch(() => { if (rid === reqRef.current) setResults([]); })
        .finally(() => { if (rid === reqRef.current) setLoading(false); });
    }, 250);
    return () => clearTimeout(timer);
  }, [q, country, open]);

  const pick = (name) => { onChange?.(name); setQ(name); setOpen(false); };
  const typed = (q || "").trim();
  const exact = results.some((c) => c.name.toLowerCase() === typed.toLowerCase());

  const panel = open && rect ? createPortal(
    <div
      className="rounded-xl border shadow-lg overflow-y-auto"
      style={{ position: "fixed", top: rect.top, bottom: rect.bottom, left: rect.left, width: rect.width, maxHeight: rect.maxHeight, zIndex: 9999, pointerEvents: "auto", borderColor: "var(--yalola-border)", background: "var(--yalola-surface)" }}
      data-testid={`${testId}-results`}
      data-placement={rect.flip ? "above" : "below"}
    >
      {!country ? (
        <div className="px-3 py-3 text-xs" style={{ color: "var(--yalola-text-muted)" }}>{tr(NEED_COUNTRY)}</div>
      ) : loading ? (
        <div className="px-3 py-3 text-xs flex items-center gap-1.5" style={{ color: "var(--yalola-text-muted)" }}><Loader2 size={13} className="animate-spin" /> …</div>
      ) : (
        <>
          {results.map((c) => (
            <button key={`${c.name}-${c.region}`} type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => pick(c.name)} className="w-full flex items-center gap-2 px-3 py-2 text-left transition-colors hover:bg-[color:var(--yalola-surface-alt)]" data-testid={`${testId}-option`}>
              <MapPin size={13} style={{ color: "var(--yalola-primary)" }} className="shrink-0" />
              <span className="flex-1 min-w-0">
                <span className="text-sm font-medium truncate block">{c.name}</span>
                {c.region && <span className="block text-[11px]" style={{ color: "var(--yalola-text-muted)" }}>{c.region}</span>}
              </span>
            </button>
          ))}
          {typed && !exact && (
            <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => pick(typed)} className="w-full text-left px-3 py-2 text-xs border-t hover:bg-[color:var(--yalola-surface-alt)]" style={{ borderColor: "var(--yalola-border)", color: "var(--yalola-text-muted)" }} data-testid={`${testId}-free`}>
              {tr(USE_FREE)} « {typed} »
            </button>
          )}
          {!results.length && !typed && (
            <div className="px-3 py-3 text-xs" style={{ color: "var(--yalola-text-muted)" }}>{tr(NONE)}</div>
          )}
        </>
      )}
    </div>,
    document.body,
  ) : null;

  return (
    <div className="relative" ref={wrapRef}>
      <MapPin size={15} className="absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: "var(--yalola-text-muted)" }} />
      <Input
        value={q}
        onChange={(e) => { setQ(e.target.value); onChange?.(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 160)}
        placeholder={placeholder || tr(PH)}
        className="pl-8"
        data-testid={testId}
      />
      {panel}
    </div>
  );
};
